/**
 * i18next configuration helpers.
 *
 * Key convention (08-pa-jp-design-reference §9, 00-spec-package-index
 * drift log):
 *
 *     efs.<module>.<screen>.<key>
 *
 * Modules use kebab-case ("monthly-batches", "mass-balance"), screens use
 * kebab-case ("list", "detail", "wizard"), keys use snake_case. Shared
 * copy lives under `efs.common.*`; exception messages under
 * `efs.exceptions.<exception_code_lowercase>`.
 *
 * Three locales are required for the ECO FAITH (日方) + 大鑫産業 (台灣方)
 * joint operation: Japanese (default), Traditional Chinese, English.
 */

export const SUPPORTED_LANGS = ['ja', 'zh-TW', 'en'] as const;
export type EfsLang = (typeof SUPPORTED_LANGS)[number];

export const DEFAULT_LANG: EfsLang = 'ja';

export const LANG_STORAGE_KEY = 'efs.lang';

export const LANG_LABELS: Record<EfsLang, string> = {
  ja: '日本語',
  'zh-TW': '繁體中文',
  en: 'English',
};

export function isSupportedLang(value: string | null | undefined): value is EfsLang {
  return !!value && (SUPPORTED_LANGS as readonly string[]).includes(value);
}

export function getStoredLang(): EfsLang | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage?.getItem(LANG_STORAGE_KEY);
    return isSupportedLang(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function setStoredLang(lang: EfsLang): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // localStorage may be unavailable (private mode, SSR, jsdom without it)
  }
}
